import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, AlertCircle, Smartphone } from "lucide-react";
import { cn } from "@/lib/utils";
import { restorePurchases } from "@/lib/purchases";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Helper function to determine subscription platform
function getSubscriptionPlatform(subscriptionProductId: string | null | undefined): 'stripe' | 'apple' | null {
  if (!subscriptionProductId) return null;
  if (subscriptionProductId.startsWith('revenuecat_')) return 'apple';
  if (subscriptionProductId.startsWith('prod_')) return 'stripe';
  return null;
}

interface PurchaseMethodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  webPrice: string;
  applePrice: string;
  onWebPurchase: () => void;
  onApplePurchase: () => void;
  isProcessing?: boolean;
  showWebOption?: boolean; // Hide web option for non-US users
  currentSubscriptionProductId?: string | null; // Current subscription to detect conflicts
  isPremium?: boolean; // Whether user has active subscription
  isForCredits?: boolean; // True if this is for credits purchase, not subscription
  trialAvailable?: boolean; // Whether user is eligible for free trial
}

export function PurchaseMethodDialog({
  open,
  onOpenChange,
  title,
  webPrice,
  applePrice,
  onWebPurchase,
  onApplePurchase,
  isProcessing = false,
  showWebOption = true,
  currentSubscriptionProductId,
  isPremium = false,
  isForCredits = false,
  trialAvailable = false,
}: PurchaseMethodDialogProps) {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<"web" | "apple" | null>(null);
  const [restoringPurchases, setRestoringPurchases] = useState(false);

  // Detect current subscription platform
  const currentPlatform = getSubscriptionPlatform(currentSubscriptionProductId);
  
  // For subscriptions only (not credits), check for platform conflicts
  const hasStripeSubscription = !isForCredits && isPremium && currentPlatform === 'stripe';
  const hasAppleSubscription = !isForCredits && isPremium && currentPlatform === 'apple';

  const handleWebPurchase = () => {

    // Block if user has Apple subscription
    if (hasAppleSubscription) {
      toast.error(
        "You have an active Apple subscription. Please cancel it in the App Store before switching to web payments.",
        { duration: 6000 }
      );
      return;
    }
    setSelectedMethod("web");
    onWebPurchase();
  };

  const handleApplePurchase = () => {
    // Block if user has Stripe subscription
    if (hasStripeSubscription) {
      toast.error(
        "You have an active Stripe subscription. Please cancel it in Account Settings before switching to Apple.",
        { duration: 6000 }
      );
      return;
    }
    setSelectedMethod("apple");
    onApplePurchase();
  };

  const handleRestorePurchases = async () => {
    setRestoringPurchases(true);
    try {
      const result = await restorePurchases();
      
      if (result.success) {
        toast.success(t('notifications.toast.purchases_restored_successfully_please_restart_the'));
        onOpenChange(false);
      } else {
        toast.error(result.error || "Failed to restore purchases. Please try again.");
      }
    } catch (error) {
      console.error('[PurchaseMethodDialog] Restore error:', error);
      toast.error(t('notifications.toast.an_error_occurred_while_restoring_purchases'));
    } finally {
      setRestoringPurchases(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">{title}</DialogTitle>
          {hasStripeSubscription && (
            <DialogDescription className="text-center">{t('ui.text.you_have_an_active_subscription_on_a_different_pla')}</DialogDescription>
          )}
        </DialogHeader>
        
        {/* Show warning only for Stripe subscription conflicts when trying to use Apple */}
        {hasStripeSubscription && (
          <Alert variant="destructive" className="my-2">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t('ui.text.you_have_an_active')}<strong>{t('ui.text.stripe_subscription')}</strong>. To switch to Apple, please cancel your current subscription in Account Settings first.
            </AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-3 py-4">
          {/* Apple In-App Purchase Button */}
          <Button
            onClick={handleApplePurchase}
            disabled={isProcessing || hasStripeSubscription}
            className={cn(
              "w-full h-auto py-4 px-6 flex flex-col items-center gap-2",
              "bg-black hover:bg-black/90 text-white",
              "border-2 border-black/10",
              hasStripeSubscription && "opacity-50 cursor-not-allowed"
            )}
            size="lg"
          >
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              <span className="font-semibold">{t('ui.text.buy_in_app')}</span>
            </div>
            <span className="text-xl font-semibold">
              {trialAvailable && !isPremium ? "Start Trial / FREE" : `${applePrice}`}
            </span>
          </Button>

          {/* Restore Purchases link - only show for subscriptions, not credits */}
          {!isForCredits && (
            <div className="pt-2 text-center">
              <button
                onClick={handleRestorePurchases}
                disabled={restoringPurchases || isProcessing}
                className="text-xs text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {restoringPurchases ? "Restoring..." : "Restore Purchases"}
              </button>
            </div>
          )}

          {/* Web Purchase Button - only show if available */}
          {/* HIDDEN: Web payment option temporarily disabled
          {showWebOption && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <Button
                onClick={handleWebPurchase}
                disabled={isProcessing}
                className={cn(
                  "w-full h-auto py-3 px-4 flex items-center justify-between gap-2",
                  "bg-background hover:bg-muted/50 border border-border/50"
                )}
                variant="ghost"
                size="sm"
              >
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                  <ExternalLink className="w-3 h-3" />
                  <span>{t('ui.text.get_externally_with')}</span>
                  <svg className="w-10 h-4 inline-block" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                    <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z" fillRule="evenodd"/>
                  </svg>
                </div>
                <span className="text-sm text-muted-foreground">
                  {webPrice}
                </span>
              </Button>

              <p className="text-[9px] text-center text-muted-foreground/60 px-4 -mt-1">{t('ui.text.alternative_payment_option')}</p>
            </>
          )}
          */}
        </div>
      </DialogContent>
    </Dialog>
  );
}
